import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useUniqueValues } from '../../hooks/useLeadImportApi';

interface ValueMapEditorProps {
  fileID: string;
  columnName: string;
  valueMap: Record<string, string>;
  onChange: (valueMap: Record<string, string>) => void;
  options: { id: string; title: string }[];
}

/**
 * Fetches unique values from a selected column and displays a two-column mapping UI.
 * Left column: the unique value from the file.
 * Right column: an Autocomplete to pick the matching system value (stage/disposition).
 */
const ValueMapEditor: React.FC<ValueMapEditorProps> = ({
  fileID,
  columnName,
  valueMap,
  onChange,
  options,
}) => {
  const uniqueValuesMutation = useUniqueValues();

  useEffect(() => {
    if (fileID && columnName) {
      uniqueValuesMutation.mutate({ fileID, columnName });
    }
  }, [fileID, columnName]);

  const handleMappingChange = (uniqueValue: string, optionId: string) => {
    const newMap = { ...valueMap, [uniqueValue]: optionId };
    onChange(newMap);
  };

  if (uniqueValuesMutation.isPending) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading unique values...</Typography>
      </Box>
    );
  }

  if (uniqueValuesMutation.isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load unique values for column &quot;{columnName}&quot;.
      </Alert>
    );
  }

  if (!uniqueValuesMutation.data) {
    return null;
  }

  // Normalize response
  const rawData = uniqueValuesMutation.data as any;
  const values: string[] = Array.isArray(rawData)
    ? rawData
    : Array.isArray(rawData.values)
    ? rawData.values
    : Array.isArray(rawData.unique_values)
    ? rawData.unique_values
    : Array.isArray(rawData.uniqueValues)
    ? rawData.uniqueValues
    : [];
  const truncated = rawData.truncated ?? false;
  const totalCount = rawData.totalCount ?? rawData.total_count ?? values.length;

  return (
    <Box sx={{ mt: 2 }} data-testid="value-map-editor">
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Map Column Values
      </Typography>

      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Showing {values.length} of {totalCount} unique values. Not all values
          can be displayed due to the service threshold.
        </Alert>
      )}

      {/* Header row */}
      {values.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            mb: 1,
            px: 1,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="bold"
          >
            File Value
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="bold"
          >
            Map To
          </Typography>
        </Box>
      )}

      {/* Value rows */}
      <Box
        sx={{
          maxHeight: 300,
          overflowY: 'auto',
          border: values.length > 0 ? '1px solid' : 'none',
          borderColor: 'divider',
          borderRadius: 1,
          p: values.length > 0 ? 1 : 0,
        }}
      >
        {values.map((value) => (
          <Box
            key={value}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
              alignItems: 'center',
              py: 0.5,
              '&:not(:last-child)': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: 1,
                mb: 0.5,
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={value}
            >
              {value}
            </Typography>

            <Autocomplete
              options={options}
              getOptionLabel={(opt) => opt.title}
              value={
                options.find((o) => o.id === (valueMap[value] ?? '')) || null
              }
              onChange={(_e, val) => handleMappingChange(value, val?.id || '')}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select..." size="small" />
              )}
              ListboxProps={{ style: { maxHeight: 200 } }}
              size="small"
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />
          </Box>
        ))}
      </Box>

      {values.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No unique values found in this column.
        </Typography>
      )}
    </Box>
  );
};

export default ValueMapEditor;
